import { useMemo } from "react";
import { animate as animateValue, useAnimate } from "framer-motion";
import type { AnimationScope } from "framer-motion";

type ScopedAnimate = ReturnType<typeof useAnimate>[1];

/**
 * `useAnimate` for the icons in this folder: same scope, same `animate`, except
 * that animating an icon that has left the screen does nothing.
 *
 * An icon can be asked to animate after it is gone. Hover callbacks are
 * delivered after the next render, so a pointer that enters a button and clicks
 * it in the same frame reaches `start()` once the click has already removed the
 * button (the chat swaps its message box on the first send; a modal closes).
 * The same happens to a later step of an animation that awaits the previous
 * one. Plain `animate(".selector")` then looks inside a scope with no element
 * and throws "Cannot read properties of null (reading 'querySelectorAll')".
 */
// The default mirrors useAnimate's own, so the scope fits the <svg> or <div> of any icon
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useIconAnimate<T extends Element = any>(): [AnimationScope<T>, ScopedAnimate] {
  const [scope, animate] = useAnimate<T>();

  const animateIfMounted = useMemo(() => {
    const run = animate as (...args: unknown[]) => ReturnType<ScopedAnimate>;
    const guarded = (...args: unknown[]) =>
      scope.current
        ? run(...args)
        : // Already-finished controls, so a caller can still await or stop them
          animateValue(0, 0, { duration: 0 });
    return guarded as ScopedAnimate;
  }, [animate, scope]);

  return [scope, animateIfMounted];
}
